package diana.soleil.design.iraniancanadienbazzar.core.model

import kotlinx.serialization.Serializable

/**
 * A business listing in the directory. Mirrors `businesses/{businessId}` in `docs/DATA_MODEL.md` and
 * the seed shape in `firebase/seed/businesses.firebase.json`.
 *
 * Server-managed timestamps (`createdAt`/`updatedAt`) are intentionally omitted — they are not needed
 * for display and keep this model free of any Firestore timestamp type, so it stays pure-Kotlin.
 * Aggregate fields ([rating], [reviewCount]) are maintained by Cloud Functions, never written here.
 */
@Serializable
data class Business(
    val id: String,
    val name: String,
    val nameLocalized: LocalizedText? = null,
    val serviceTitle: LocalizedText? = null,
    val ownerUid: String? = null,
    val claimStatus: ClaimStatus = ClaimStatus.Unclaimed,
    /** Who asked to claim this listing (set with the claim_requested flip; admin grants to this uid). */
    val claimRequestedBy: String? = null,
    val categoryId: String,
    val description: LocalizedText = LocalizedText(),
    val status: BusinessStatus = BusinessStatus.Pending,
    val verified: Boolean = false,
    val phone: String? = null,
    val email: String? = null,
    val website: String? = null,
    val address: Address,
    /**
     * Suppress the exact street address + map pin (home-based/residential listings). When true only
     * the neighbourhood (`city, province`) is shown publicly — the street line and directions are
     * hidden — so a listing registered at someone's home doesn't publish their home. Owner/admin set.
     */
    val hideExactAddress: Boolean = false,
    /**
     * Owner opt-out of the public directory. When true the listing is hidden from public discovery
     * (rules deny public read) even while `status == Approved`; the owner still sees it in their
     * dashboard. Lets an owner who doesn't want to be publicly identified remove themselves without
     * deleting the record. Owner/admin set.
     */
    val ownerHidden: Boolean = false,
    val metroId: String? = null,
    val hours: Map<String, String> = emptyMap(),
    val logoUrl: String? = null,
    val coverUrl: String? = null,
    val photos: List<String> = emptyList(),
    val mediaStatus: MediaStatus = MediaStatus.CategoryPlaceholderPending,
    val languagesSpoken: List<String> = emptyList(),
    /** Enabled structured-fact keys (category-scoped; see [BusinessAttributeRegistry]). */
    val attributes: List<String> = emptyList(),
    val offersChat: Boolean = false,
    val offersOrdering: Boolean = false,
    /** Price tier 1–4 (→ $ … $$$$), or null/0 when unknown/hidden. Shown on cards & detail per Stitch. */
    val priceLevel: Int? = null,
    val rating: Double = 0.0,
    val reviewCount: Int = 0,
    /** Community endorsements ("Recommended by N neighbours"), Function-maintained — never client-written. */
    val endorsementCount: Int = 0,
    val sponsorship: Sponsorship = Sponsorship(),
    /**
     * The listing's owner has an active Mahaleh Pro subscription (denormalized from `users/{uid}.pro`
     * by Functions — never client-written). Drives the public "Pro" badge + a non-sponsored ranking
     * boost.
     */
    val ownerPro: Boolean = false,
    val source: BusinessSource? = null,
    /**
     * Owner edited the listing AFTER approval (BIZ-5) — Function-set, admin re-approval clears.
     * Surfaces the listing in the admin re-review queue; the listing stays live meanwhile.
     */
    val editedSinceApproval: Boolean = false,
) {
    /** Display name for [language]: the localized script variant if provided, else the canonical name. */
    fun displayName(language: Language): String =
        nameLocalized?.resolve(language)?.takeIf { it.isNotBlank() } ?: name

    /** "$".."$$$$" for [priceLevel] 1–4, or null when unknown/hidden. */
    fun priceLabel(): String? = priceLevel?.takeIf { it in 1..4 }?.let { "$".repeat(it) }

    /**
     * The card/detail subtitle line: the localized service title with the price tier appended when
     * known — e.g. "Persian Restaurant · $$". Falls back to just one part (or "") when the other is
     * absent, so photoless/priceless listings still read cleanly. Matches the Stitch business cards.
     */
    fun serviceLine(language: Language): String =
        listOfNotNull(
            serviceTitle?.resolve(language)?.takeIf { it.isNotBlank() },
            priceLabel(),
        ).joinToString(" · ")

    /** Public address line: neighbourhood only when [hideExactAddress], else the full street address. */
    fun displayAddress(): String = if (hideExactAddress) address.area() else address.formatted()

    /** Whether the exact location (street line, map pin, directions) may be shown. */
    val showsExactLocation: Boolean get() = !hideExactAddress

    /** True when the listing is publicly visible per Firestore rules. */
    val isPublished: Boolean get() = status == BusinessStatus.Approved && !ownerHidden

    /** True when this listing can be claimed by a signed-in user (no owner yet). */
    /** Claimable in-app: unclaimed AND not verified — verified listings re-claim via admin email. */
    val isClaimable: Boolean get() = ownerUid == null && claimStatus == ClaimStatus.Unclaimed && !verified
}
